import {
  RouteObject,
  useRoutes,
  BrowserRouter,
  useNavigate,
  useParams,
  Navigate,
  useLocation,
} from 'react-router-dom';
import { useSession } from '~/components/contexts/SessionProvider';
import { generateAuthRoutes } from './utils';
import {
  IGSuccess,
  Login,
  OnboardingScreen,
  Page404Screen,
  Profile,
  VerifyEmail,
  AgentDashboard,
  ResetPassword,
} from '../screens';
import Referrals from '../screens/referrals';
import Layout from '../layout/Layout';
import { motion } from 'motion/react';
import { useEffect } from 'react';
import CalendarPage from '../screens/calendar';
import { TypingEffect } from '../shared/TypingEffect';
import GoogleDriveIntegration from '../screens/integrations/googleDriveIntegration';
import { trackPageView } from '~/lib/analytics';
import { isAdmin } from '~/lib/admin';
import AdminAnalytics from '../screens/admin/AdminAnalytics';

export const Router = () => {
  return (
    <BrowserRouter>
      <InnerRouter />
    </BrowserRouter>
  );
};

const PrivateRedirect = () => {
  const navigate = useNavigate();
  const { owner } = useSession();

  useEffect(() => {
    if (owner!.agents.length === 0) {
      navigate('/create_agent');
    } else {
      navigate(`/agent/${owner!.agents[0]._id}/dashboard`);
    }
  }, [owner, navigate]);

  return null;
};

const MagicLinkVerify = () => <VerifyEmail type="magic_link" />;
const EmailVerify = () => <VerifyEmail type="email_verification" />;

const PublicRoutes = () =>
  useRoutes([
    {
      path: '/',
      children: [
        { index: true, element: <Login /> },
        { path: 'calendar/:schedule_id/*', element: <CalendarPage /> },
        { path: 'verify', element: <MagicLinkVerify /> },
        { path: 'verify-email', element: <EmailVerify /> },
        { path: 'reset-password', element: <ResetPassword /> },
        { path: '*', element: <Navigate to="/" /> }, // Redirect to index
      ],
    },
  ]);

const AgentRoutes = () => {
  const { agentId } = useParams();
  const { owner } = useSession();
  const navigate = useNavigate();

  if (agentId === 'create_agent') navigate('/create_agent');

  const agent = owner?.agents.find((a) => a._id === agentId);

  if (!agent) {
    return <Page404Screen />;
  }

  const routes = generateAuthRoutes(agent);

  // Add dashboard route
  routes.unshift({
    path: 'dashboard',
    element: <AgentDashboard />,
  });

  return useRoutes(routes);
};

const PrivateRoutes = () => {
  const location = useLocation();
  const { owner } = useSession();

  const routes: RouteObject[] = [
    {
      path: '/',
      element: <Layout />,
      children: [
        { index: true, element: <PrivateRedirect /> },
        { path: 'create_agent', element: <OnboardingScreen /> },
        { path: 'instagram/success', element: <IGSuccess /> },
        { path: 'googleDrive/success', element: <GoogleDriveIntegration /> },
        { path: 'agent/:agentId/*', element: <AgentRoutes /> },
        { path: 'profile', element: <Profile /> },
        { path: 'calendar/:schedule_id/*', element: <CalendarPage /> },
        { path: '*', element: <Page404Screen /> },
      ],
    },
  ];

  // Add admin routes for admin users only
  if (isAdmin(owner.email)) {
    routes[0].children!.splice(-1, 0, {
      path: 'admin/analytics',
      element: <AdminAnalytics />,
    });
  }

  // Add referrals route for owners who are invited to the referrals program
  if (owner.allow_referral) {
    routes[0].children!.splice(-1, 0, {
      path: 'referrals',
      element: <Referrals />,
    });
  }

  // Track page views on route changes
  useEffect(() => {
    trackPageView(location.pathname, document.title);
  }, [location]);

  return useRoutes(routes);
};

const Loader = () => (
  <motion.div
    key={'loader'}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="bg-base-100 absolute inset-0 m-auto flex h-full w-full flex-col items-center justify-center gap-10"
  >
    <div data-testid="loading" className="loader" />
    <TypingEffect />
  </motion.div>
);

const InnerRouter = () => {
  const { owner, loading } = useSession();

  if (loading) {
    return <Loader />;
  }

  if (!owner._id) {
    return <PublicRoutes />;
  }

  return <PrivateRoutes />;
};
